import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth, useToast } from "@/contexts";
import { getCurrentOrg, leaveOrg } from "@/features/organizations/api";

export default function OrgLeave() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [orgName, setOrgName] = useState("");
  const [orgId, setOrgId] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data, error } = await getCurrentOrg();
      if (error) {
        toast.showAlert("Error", error.message, "error");
        navigate({ to: "/dashboard" });
        return;
      }
      if (data) {
        setOrgName(data.name);
        setOrgId(data.id);
        setIsOwner(data.role === "owner");
      }
    })();
  }, [user, toast, navigate]);

  async function handleLeave() {
    if (!orgId) return;
    setLeaving(true);
    const { error } = await leaveOrg(orgId);
    if (error) {
      toast.showAlert("Error", error.message, "error");
      setLeaving(false);
    } else {
      toast.showAlert("Left", `You have left ${orgName}.`, "success");
      navigate({ to: "/dashboard", replace: true });
    }
  }

  if (isOwner) {
    return (
      <div className="flex flex-col gap-4 items-center py-8">
        <span className="text-[2rem]">🚫</span>
        <span className="text-text text-[1.125rem] font-black text-center">
          Owners cannot leave
        </span>
        <p className="text-text-muted text-sm text-center max-w-sm">
          As the owner of <strong>{orgName}</strong>, you must transfer ownership to another member
          before you can leave.
        </p>
        <button
          type="button"
          onClick={() => navigate({ to: "/org/settings" })}
          className="bg-purple rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text cursor-pointer"
        >
          Go to settings
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 items-center py-8">
      <span className="text-[2rem]">👋</span>
      <span className="text-text text-[1.125rem] font-black text-center">Leave {orgName}?</span>
      <p className="text-text-muted text-sm text-center max-w-sm">
        You will lose access to all organization resources. You can join another org later.
      </p>
      <div className="flex gap-3 mt-2">
        <button
          type="button"
          onClick={() => navigate({ to: "/org" })}
          className="bg-card rounded-[16px] py-3 px-5 text-text text-[0.875rem] font-extrabold border border-text cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleLeave}
          disabled={leaving}
          className="bg-[#cc0000] rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text cursor-pointer disabled:opacity-50"
        >
          {leaving ? "Leaving..." : "Leave organization"}
        </button>
      </div>
    </div>
  );
}
